import { useTranslation } from 'react-i18next'

import { Box, Text, VStack } from '@enechain/ecloud-designsystem'

import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { formatDateTimeFull } from '~/models/dateTime'

type Props = {
  createdBy: string | undefined
  createdAt: Date | undefined
  updatedBy: string | undefined
  updatedAt: Date | undefined
}

/**
 * 共通 AuditPanel コンポーネント。
 *
 * 作成者 / 作成日時 / 更新者 / 更新日時を表示する。
 * 更新行は以下の場合に省略する:
 *   - updatedAt が undefined
 *   - createdAt が定義されていて updatedAt === createdAt (= 作成時刻と一致)
 * createdAt が undefined で updatedAt のみ定義されているケース (= list cache
 * から updated_at のみ渡されるケース) では更新行を **表示する**。
 *
 * created_by / updated_by は eauth member ID (= auth0 subject) を受け取り、
 * `useMemberDisplayName` で表示名へ解決して表示する。 解決できない場合は '—' を
 * 表示し、 生の member ID は画面に出さない。
 *
 * 15 タブ × CRUD の Edit モーダルで再利用することを前提に設計。
 */
export const AuditPanel = ({
  createdBy,
  createdAt,
  updatedBy,
  updatedAt,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()

  // updatedAt が無ければ非表示、 createdAt と一致するなら非表示。
  // createdAt が undefined のときは更新行を表示する (= updatedAt のみある
  // ケースで update 情報を捨てない、 Devin Review #341 BUG_0002)。
  const showUpdated =
    updatedAt !== undefined &&
    (createdAt === undefined || updatedAt.getTime() !== createdAt.getTime())

  return (
    <Box
      marginTop={4}
      padding={3}
      fontSize="sm"
      borderWidth="1px"
      borderRadius="md"
    >
      <VStack align="stretch" gap={1}>
        {/* 意図的視覚変更 (10px→12px)。design admin/occto.html:656 等の監査情報
            タイトル inline style = sm/600 が正、ユーザー承認済み。 */}
        <Text
          textStyle="captionStrong"
          marginBottom={1}
          color="Text.midEmphasis"
        >
          {t('common:auditPanel.title')}
        </Text>
        <AuditRow
          by={resolveDisplayName(createdBy)}
          label={t('common:auditPanel.createdAt')}
          timestamp={createdAt}
        />
        {showUpdated && (
          <AuditRow
            by={resolveDisplayName(updatedBy)}
            label={t('common:auditPanel.updatedAt')}
            timestamp={updatedAt}
          />
        )}
      </VStack>
    </Box>
  )
}

type AuditRowProps = {
  label: string
  by: string | undefined
  timestamp: Date | undefined
}

const AuditRow = ({
  label,
  by,
  timestamp,
}: AuditRowProps): React.ReactElement => {
  const formatted =
    timestamp !== undefined ? formatDateTimeFull(timestamp) : '—'
  const byText = by ?? '—'

  return (
    <Text color="Text.midEmphasis" fontSize="xs">
      {label}: {byText} {formatted}
    </Text>
  )
}
